import React, { useEffect, useRef, useState } from "react";
import { Bell, ChevronDown, Search, LogOut, User } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Logo from "../../Images/Logo.svg";

const Header = ({ userData }) => {
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [imageError, setImageError] = useState(false);
  const dropdownRef = useRef(null);

  // Normalize: Instructor → data.user | Student → userData directly
  const normalizedUser = userData?.user || userData || {};

  // Extract Name (Instructor uses full_name, Student uses name)
  const nameToUse =
    normalizedUser.full_name ||
    normalizedUser.name ||
    normalizedUser.username ||
    normalizedUser.fullName ||
    "User";

  // Extract Role for both Student + Instructor APIs
  const userRole =
    normalizedUser.roles?.[0]?.role?.name || // Instructor → roles[0].role.name
    normalizedUser.roles?.[0]?.name || // Student → roles[0].name
    normalizedUser.role ||
    normalizedUser.user_role ||
    normalizedUser.designation ||
    "User";

  // Avatar Handling
  const avatarUrl =
    normalizedUser.avatar_url ||
    null;

  const hasAvatar = avatarUrl && !imageError;

  // Initials for fallback avatar
  const userInitials = nameToUse
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase();

  const handleLogout = () => {
    localStorage.removeItem("access_token");
    navigate("/login");
  };

  const handleImageError = () => setImageError(true);

  // Close dropdown when clicking outside
  useEffect(() => {
    const clickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", clickOutside);
    return () => document.removeEventListener("mousedown", clickOutside);
  }, []);

  return (
    <header className="w-full h-16 bg-white shadow-sm flex items-center justify-between px-6">
      <div>
        <img src={Logo} alt="SkillsMD Logo" className="w-32" />
      </div>

      <div className="flex items-center gap-6">
        <Search className="w-5 h-5 text-gray-600 cursor-pointer" />

        <div className="relative">
          <Bell className="w-5 h-5 text-gray-600 cursor-pointer" />
          <span className="absolute top-0 right-0 bg-red-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">
            9
          </span>
        </div>

        {/* User Section */}
        <div
          className="relative flex items-center gap-2 cursor-pointer"
          onClick={() => setDropdownOpen(!dropdownOpen)}
          ref={dropdownRef}
        >
          <div className="w-10 h-10 rounded-full overflow-hidden border-2 bg-gray-100">
            {hasAvatar ? (
              <img
                src={avatarUrl}
                alt="avatar"
                className="w-full h-full object-cover"
                onError={handleImageError}
              />
            ) : (
              <div className="w-full h-full bg-[#4AACC5] flex items-center justify-center text-white font-bold">
                {userInitials}
              </div>
            )}
          </div>

          <div className="flex flex-col">
            <span className="text-sm font-semibold text-gray-800">
              {nameToUse}
            </span>
            <span className="text-xs text-gray-500">{userRole}</span>
          </div>

          <ChevronDown
            className={`w-4 h-4 text-gray-600 transition ${
              dropdownOpen ? "rotate-180" : ""
            }`}
          />

          {dropdownOpen && (
            <div className="absolute right-0 top-12 w-48 bg-white border shadow-lg rounded-lg z-50">
              <button
                onClick={() => navigate("/profile")}
                className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
              >
                <User className="inline w-4 h-4 mr-2" />
                View Profile
              </button>

              <button
                onClick={handleLogout}
                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 border-t"
              >
                <LogOut className="inline w-4 h-4 mr-2" />
                Sign Out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
